import { Toast, useToast } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import {
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude,
  useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';

const INVALIDATION_META = {
  invalidates: ['getProperties', 'spotlightOverview'] as Array<string>,
};

export const useSpotlightBatchMutations = () => {
  const { enqueueToast } = useToast();

  const { mutateAsync: batchExcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchExclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch exclude listings from spotlight' },
    },
  });

  const { mutateAsync: batchPinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchPin({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch pin listings in spotlight' },
    },
  });

  const { mutateAsync: batchUnexcludeMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnexclude({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch unexclude listings from spotlight' },
    },
  });

  const { mutateAsync: batchUnpinMutation } = useRentalManagerUserPropertySpotlightV2WebApiHandler_batchUnpin({
    meta: {
      ...INVALIDATION_META,
      context: { api: 'spotlight', details: 'batch unpin listings from spotlight' },
    },
  });

  const executeBatch = async ({
    listingAliases,
    mutationFn,
    successMessage,
    errorMessage,
    toastId,
  }: {
    listingAliases: Array<ListingAlias>;
    mutationFn: (
      aliases: Array<ListingAlias>,
    ) => Promise<{ results: Array<{ listingAlias: ListingAlias; success: boolean }> }>;
    successMessage: (successCount: number, failCount: number) => string;
    errorMessage: string;
    toastId: string;
  }) => {
    try {
      const response = await mutationFn(listingAliases);
      const successCount = response.results.filter((r) => r.success).length;
      const failCount = response.results.length - successCount;
      enqueueToast(
        <Toast id={toastId} tone={failCount > 0 ? 'warning' : 'success'}>
          {successMessage(successCount, failCount)}
        </Toast>,
      );
    } catch (error) {
      enqueueToast(
        <Toast id={`${toastId}-error`} tone="critical">
          {errorMessage}
        </Toast>,
      );
      logError(error);
    }
  };

  const batchExclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchExcludeMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} excluded. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} excluded from Spotlight.`,
      errorMessage: 'Failed to exclude listings. Try again.',
      toastId: 'spotlight-batch-exclude',
    });

  const batchPin = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchPinMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} pinned. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} pinned to Spotlight.`,
      errorMessage: 'Failed to pin listings. Try again.',
      toastId: 'spotlight-batch-pin',
    });

  const batchUnexclude = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnexcludeMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} unexcluded. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} returned to auto-optimize.`,
      errorMessage: 'Failed to unexclude listings. Try again.',
      toastId: 'spotlight-batch-unexclude',
    });

  const batchUnpin = (listingAliases: Array<ListingAlias>) =>
    executeBatch({
      listingAliases,
      mutationFn: (aliases) => batchUnpinMutation({ postData: { listingAliases: aliases } }),
      successMessage: (s, f) =>
        f > 0
          ? `${s} listing${s !== 1 ? 's' : ''} unpinned. ${f} could not be updated.`
          : `${s} listing${s !== 1 ? 's' : ''} returned to auto-optimize.`,
      errorMessage: 'Failed to unpin listings. Try again.',
      toastId: 'spotlight-batch-unpin',
    });

  return { batchExclude, batchPin, batchUnexclude, batchUnpin };
};
